// AI agents (MCP): the API calls behind Settings → MCP and the approval page,
// and how to connect each agent. The backend is routes/agents.py.
import axios from 'axios';
import { API_URL } from './api';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

export const agentsApi = {
  list: () => axios.get(`${API_URL}/agents`, auth()).then((r) => r.data),
  createToken: (name) => axios.post(`${API_URL}/agents/tokens`, { name }, auth()).then((r) => r.data),
  revokeToken: (id) => axios.delete(`${API_URL}/agents/tokens/${id}`, auth()).then((r) => r.data),
  disconnect: (id) => axios.delete(`${API_URL}/agents/connections/${id}`, auth()).then((r) => r.data),
  request: (id) => axios.get(`${API_URL}/agents/requests/${id}`, auth()).then((r) => r.data),
  approve: (id) => axios.post(`${API_URL}/agents/requests/${id}/approve`, {}, auth()).then((r) => r.data.redirect),
  deny: (id) => axios.post(`${API_URL}/agents/requests/${id}/deny`, {}, auth()).then((r) => r.data.redirect),
};

// "a, b and c"
const list = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

// What an agent can do once connected, shown on the approval page. It
// follows the account's plan (`onPlan` is get_account's `on_this_plan`, from
// backend/mcp_rules.account_features), so nobody is promised briefs or crew
// their plan doesn't have. Keep in step with the tools in backend/mcp_server.py.
export function agentCan(onPlan = {}, canCreate = true) {
  const crew = onPlan.crew_and_suppliers;
  const briefs = onPlan.briefs;
  const read = ['jobs', 'clients', 'equipment', ...(crew ? ['crew'] : []), ...(briefs ? ['briefs'] : [])];
  if (!canCreate) return [`Read your ${list(read)}`];
  const change = ['estimates', 'clients', ...(crew ? ['crew'] : []), ...(briefs ? ['briefs'] : []),
    ...(onPlan.shot_lists ? ['shot lists'] : [])];
  return [
    `Read your ${list(read)}`,
    `Draft and change ${list(change)}`,
    'Add equipment, its income history and clients from your files',
  ];
}

export const AGENT_CANT = [
  'Send anything to a client, or push anything to Xero',
  'Delete anything (imports it made can be undone)',
];

// How to connect each agent. Menus move between versions, so the steps name
// the place to look rather than every click. `Backticks` mark a command, and
// <address> is replaced with the account's MCP address.
export const AGENT_GUIDES = [
  {
    id: 'claude',
    name: 'Claude',
    steps: [
      'In Claude (web or desktop), open Settings, then Connectors.',
      'Choose Add custom connector. Name it No Half Days and paste the address above.',
      'Choose Connect, sign in to No Half Days and approve.',
    ],
    note: 'Works on every Claude plan. On a Team or Enterprise plan, an owner adds the connector first, under Organization settings.',
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    steps: [
      'In ChatGPT settings, under Apps, turn on Developer mode (in Advanced settings).',
      'Add a custom MCP server: name it No Half Days, paste the address above and choose OAuth.',
      'Sign in to No Half Days and approve.',
    ],
    note: 'Making changes needs ChatGPT Business or Enterprise. On Pro, ChatGPT can read your account but not change it.',
  },
  {
    id: 'gemini',
    name: 'Gemini',
    steps: [
      'On gemini.google.com (the website, not the app), open Settings, then Connected apps.',
      'Add a custom app and paste the address above.',
      'Sign in to No Half Days and approve. It then works in the Gemini app too.',
    ],
    note: 'New in Gemini, and for personal Google accounts in the US for now.',
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    steps: [
      'In Perplexity, open Settings, then Connectors.',
      'Add a custom connector, choose Remote, paste the address above and choose OAuth.',
      'Sign in to No Half Days and approve.',
    ],
    note: 'Needs Perplexity Pro, Max or Enterprise.',
  },
  {
    id: 'developer',
    name: 'Developer tools',
    steps: [
      'Claude Code: run `claude mcp add --transport http nohalfdays <address>`, then `/mcp` to sign in.',
      'Cursor, VS Code with GitHub Copilot, and Gemini CLI: add the address as a remote (HTTP) MCP server and sign in when asked.',
      'Or make a personal token below and add it as an Authorization header.',
    ],
    note: null,
  },
];
